/**
 * Modern Interactions & Animations for GoalTickets
 * Premium user experience with smooth animations
 */

class GoalTicketsEnhancements {
  constructor() {
    this.init();
  }

  init() {
    this.initScrollAnimations();
    // this.initCountdownTimers(); // Disabled - CLS fix
    this.initParallaxEffects();
    this.initTicketCards();
    // this.initNotifications(); // Disabled - CLS fix
    // this.initFloatingActionButton(); // Disabled FAB
    // this.initDarkMode(); // Disabled dark mode
    this.initLoadingStates();
    this.initTrustBadges();
    this.initVideoBackgrounds();
    this.initMicroInteractions();
  }

  /**
   * Scroll-triggered animations
   */
  initScrollAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('revealed');
            entry.target.style.animationDelay = `${index * 0.1}s`;
          }, index * 100);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.scroll-reveal').forEach(el => {
      observer.observe(el);
    });

    // Add stagger animation class
    document.querySelectorAll('.stagger-animation').forEach((container) => {
      const children = container.children;
      Array.from(children).forEach((child, index) => {
        child.style.animationDelay = `${index * 0.1}s`;
        child.classList.add('scroll-reveal');
      });
    });
  }

  /**
   * Event countdown timers
   */
  initCountdownTimers() {
    const countdowns = document.querySelectorAll('[data-countdown]');
    
    countdowns.forEach(countdown => {
      const targetDate = new Date(countdown.dataset.countdown).getTime();
      
      const updateCountdown = () => {
        const now = new Date().getTime();
        const distance = targetDate - now;
        
        if (distance < 0) {
          countdown.innerHTML = '<span class="countdown-expired">Event Started!</span>';
          return;
        }
        
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        
        countdown.innerHTML = `
          <div class="countdown-container">
            <div class="countdown-item">
              <span class="countdown-number">${days}</span>
              <span class="countdown-label">Days</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-number">${hours.toString().padStart(2, '0')}</span>
              <span class="countdown-label">Hours</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-number">${minutes.toString().padStart(2, '0')}</span>
              <span class="countdown-label">Minutes</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-number">${seconds.toString().padStart(2, '0')}</span>
              <span class="countdown-label">Seconds</span>
            </div>
          </div>
        `;
      };
      
      updateCountdown();
      setInterval(updateCountdown, 1000);
    });
  }

  /**
   * Parallax scrolling effects
   */
  initParallaxEffects() {
    const parallaxElements = document.querySelectorAll('.parallax');
    
    window.addEventListener('scroll', () => {
      const scrolled = window.pageYOffset;
      
      parallaxElements.forEach(el => {
        const speed = el.dataset.speed || 0.5;
        const offset = scrolled * speed;
        el.style.setProperty('--parallax-offset', `${offset}px`);
      });
    });
  }

  /**
   * Interactive ticket cards
   */
  initTicketCards() {
    const cards = document.querySelectorAll('.ticket-card');
    
    cards.forEach(card => {
      // Add tilt effect on hover
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const rotateX = (y - centerY) / 10;
        const rotateY = (centerX - x) / 10;
        
        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      });
      
      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0)';
      });
      
      // Add click ripple effect
      card.addEventListener('click', function(e) {
        const ripple = document.createElement('span');
        ripple.className = 'ripple';
        
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        
        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        
        this.appendChild(ripple);
        
        setTimeout(() => ripple.remove(), 600);
      });
    });
  }

  /**
   * Live notifications system
   */
  initNotifications() {
    const notifications = [
      { title: 'New Booking!', text: 'Someone just bought World Cup tickets', icon: '🎫' },
      { title: 'Limited Seats!', text: 'Only 4 tickets left for Champions League Final', icon: '⚡' },
      { title: 'Hot Deal!', text: '10% off French Open Finals', icon: '🔥' },
      { title: 'Just Sold!', text: 'French Open Quarter-Final tickets', icon: '🎾' }
    ];
    
    let notificationIndex = 0;
    
    const showNotification = () => {
      const notification = notifications[notificationIndex % notifications.length];
      
      const notifElement = document.createElement('div');
      notifElement.className = 'notification-badge';
      notifElement.innerHTML = `
        <div class="notification-icon">${notification.icon}</div>
        <div class="notification-content">
          <div class="notification-title">${notification.title}</div>
          <div class="notification-text">${notification.text}</div>
        </div>
      `;
      
      document.body.appendChild(notifElement);
      
      setTimeout(() => notifElement.classList.add('show'), 100);
      setTimeout(() => {
        notifElement.classList.remove('show');
        setTimeout(() => notifElement.remove(), 500);
      }, 4000);
      
      notificationIndex++;
    };
    
    // Show first notification after 5 seconds, then every 30 seconds
    setTimeout(showNotification, 5000);
    setInterval(showNotification, 30000);
  }

  /**
   * Floating Action Button
   */
  initFloatingActionButton() {
    const fabHTML = `
      <div class="fab-container">
        <button class="fab" aria-label="Quick Actions">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
        </button>
        <div class="fab-menu">
          <button class="fab-menu-item" data-action="chat" aria-label="Chat">💬</button>
          <button class="fab-menu-item" data-action="call" aria-label="Call">📞</button>
          <button class="fab-menu-item" data-action="help" aria-label="Help">❓</button>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', fabHTML);
    
    const fab = document.querySelector('.fab');
    const fabMenu = document.querySelector('.fab-menu');
    
    fab.addEventListener('click', () => {
      fab.classList.toggle('active');
      fabMenu.classList.toggle('active');
    });
    
    document.querySelectorAll('.fab-menu-item').forEach(item => {
      item.addEventListener('click', () => {
        const action = item.dataset.action;
        this.handleFabAction(action);
      });
    });
  }

  handleFabAction(action) {
    switch(action) {
      case 'chat':
        // Open chat widget
        console.log('Opening chat...');
        break;
      case 'call':
        // Show phone number
        alert('Call us: 1-800-TICKETS');
        break;
      case 'help':
        // Navigate to help page
        window.location.href = '/pages/faq';
        break;
    }
  }

  /**
   * Dark mode toggle
   */
  initDarkMode() {
    const darkModeToggle = document.createElement('button');
    darkModeToggle.className = 'dark-mode-toggle';
    darkModeToggle.innerHTML = '🌙';
    darkModeToggle.setAttribute('aria-label', 'Toggle dark mode');
    
    darkModeToggle.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      background: white;
      border: 2px solid #e0e0e0;
      border-radius: 50%;
      width: 50px;
      height: 50px;
      font-size: 1.5rem;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    `;
    
    document.body.appendChild(darkModeToggle);
    
    // Check for saved preference
    const isDarkMode = localStorage.getItem('darkMode') === 'true';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
      darkModeToggle.innerHTML = '☀️';
    }
    
    darkModeToggle.addEventListener('click', () => {
      document.body.classList.toggle('dark-mode');
      const isDark = document.body.classList.contains('dark-mode');
      darkModeToggle.innerHTML = isDark ? '☀️' : '🌙';
      localStorage.setItem('darkMode', isDark);
      
      // Add rotation animation
      darkModeToggle.style.transform = 'rotate(360deg)';
      setTimeout(() => {
        darkModeToggle.style.transform = 'rotate(0deg)';
      }, 300);
    });
  }

  /**
   * Loading states and skeleton screens
   */
  initLoadingStates() {
    // Add skeleton loading to lazy-loaded content
    const lazyContainers = document.querySelectorAll('[data-lazy-load]');
    
    lazyContainers.forEach(container => {
      const skeleton = this.createSkeletonLoader();
      container.appendChild(skeleton);
      
      // Simulate content loading
      setTimeout(() => {
        skeleton.remove();
        container.classList.add('loaded');
      }, 1500);
    });
  }

  createSkeletonLoader() {
    const skeleton = document.createElement('div');
    skeleton.className = 'skeleton-loader';
    skeleton.innerHTML = `
      <div class="skeleton skeleton-image"></div>
      <div class="skeleton skeleton-title"></div>
      <div class="skeleton skeleton-text"></div>
      <div class="skeleton skeleton-text" style="width: 80%;"></div>
    `;
    return skeleton;
  }

  /**
   * Animated trust badges
   */
  initTrustBadges() {
    // Get translations from global window object (set in theme.liquid)
    const translations = window.trustBadgeTranslations || {};
    const badges = [
      {
        icon: '✓',
        title: translations.guaranteed || '100% Guaranteed',
        desc: translations.authenticTickets || 'Authentic tickets'
      },
      {
        icon: '🔒',
        title: translations.securePayment || 'Secure Payment',
        desc: translations.sslEncrypted || 'SSL encrypted'
      },
      {
        icon: '⭐',
        title: translations.trustedSince || 'Trusted Since 2011',
        desc: translations.happyCustomers || '10,000+ happy customers'
      }
    ];
    
    const trustSection = document.querySelector('.trust-badges');
    if (!trustSection) return;
    
    badges.forEach((badge, index) => {
      const badgeElement = document.createElement('div');
      badgeElement.className = 'trust-badge';
      badgeElement.style.animationDelay = `${index * 0.2}s`;
      badgeElement.innerHTML = `
        <div class="trust-badge-icon">${badge.icon}</div>
        <div class="trust-badge-title">${badge.title}</div>
        <div class="trust-badge-desc">${badge.desc}</div>
      `;
      trustSection.appendChild(badgeElement);
    });
  }

  /**
   * Video backgrounds optimization
   */
  initVideoBackgrounds() {
    const videos = document.querySelectorAll('.hero-video-bg');
    
    videos.forEach(video => {
      // Pause video when not in viewport
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            video.play();
          } else {
            video.pause();
          }
        });
      });
      
      observer.observe(video);
      
      // Add loading animation
      video.addEventListener('loadstart', () => {
        video.classList.add('loading');
      });
      
      video.addEventListener('canplay', () => {
        video.classList.remove('loading');
        video.classList.add('loaded');
      });
    });
  }

  /**
   * Micro-interactions
   */
  initMicroInteractions() {
    // Button hover effects
    document.querySelectorAll('.btn-premium').forEach(btn => {
      btn.addEventListener('mouseenter', function(e) {
        const x = e.pageX - this.offsetLeft;
        const y = e.pageY - this.offsetTop;
        
        const ripple = document.createElement('span');
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;
        ripple.className = 'btn-ripple';
        
        this.appendChild(ripple);
        setTimeout(() => ripple.remove(), 1000);
      });
    });
    
    // Input focus effects
    document.querySelectorAll('input, textarea').forEach(input => {
      input.addEventListener('focus', function() {
        this.parentElement.classList.add('input-focused');
      });
      
      input.addEventListener('blur', function() {
        this.parentElement.classList.remove('input-focused');
      });
    });
    
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new GoalTicketsEnhancements();
  });
} else {
  new GoalTicketsEnhancements();
}

// Export for use in other scripts
window.GoalTicketsEnhancements = GoalTicketsEnhancements;